import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { Tabs, type TabItem } from '@naf-simba/sections-tabs';
import { NAFGTMEvent } from '@appTypes/GTM/NAFGTMEvent';
import { useGTMDataLayer } from '@hooks/GTM/useGTMDataLayer';
import { SectionBlockType } from '@appTypes/CategoryAppType';
import ErrorBoundary from '@components/error-component/ErrorBoundary';
import useSelector from '@redux/typedHooks';
import { updateUrlQueryValues } from '@utils/updateUrlQueryValues';
import { useActiveTab } from '@hooks/useActiveTab';
import { SectionContainer } from './Styles';
import { SectionBlocks } from '../../index';

interface TabView {
  title: string;
  slug: string;
  sections: SectionBlockType[];
}

interface Props {
  block: {
    key: string;
    type: string;
    tabViews: TabView[];
  };
}

const PARAM_NAME = 'tabView';

export const TabBlock = ({ block: { tabViews } }: Props) => {
  const dataLayer = useGTMDataLayer();
  const location = useLocation();
  const baseUrl = useSelector((state) => state.application.baseUrl);
  const { setActiveTab } = useActiveTab();

  const tabViewMap = useMemo(
    () =>
      tabViews.reduce((acc: Record<TabView['slug'], TabView>, cur) => {
        acc[cur.slug] = cur;
        return acc;
      }, {}),
    [tabViews],
  );

  const resolveSlug = useCallback(
    (param: string | null) => (param && tabViewMap[param] ? param : tabViews[0]?.slug),
    [tabViewMap, tabViews],
  );

  // From the router, so the server renders the linked tab too.
  const [selectedTabSlug, setSelectedTabSlug] = useState(() =>
    resolveSlug(new URLSearchParams(location.search).get(PARAM_NAME)),
  );

  // Tab clicks bypass the router, so read `window.location`; `location.key` catches a link that
  // repeats the current search.
  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get(PARAM_NAME);
    setSelectedTabSlug(resolveSlug(param));

    if (tabViews.length === 0) {
      setActiveTab(null);
    } else if (param && tabViewMap[param]) {
      setActiveTab(tabViewMap[param].title);
    }
  }, [location.key, resolveSlug, tabViewMap, tabViews.length, setActiveTab]);

  const handleTracking = useCallback(
    (tabSlug: string) => {
      if (!dataLayer || !tabSlug) return;
      const fromTab = tabViewMap[selectedTabSlug]?.title;
      const toTab = tabViewMap[tabSlug]?.title;

      dataLayer.push({
        event: NAFGTMEvent.tabNavigation,
        tabNavigation: {
          from: fromTab || 'UNDEFINED',
          to: toTab || 'UNDEFINED',
        },
        page: location.pathname,
        pageUrl: `${baseUrl}${location.pathname}`,
      });
    },
    [baseUrl, dataLayer, location.pathname, selectedTabSlug, tabViewMap],
  );

  const handleChange = (tabSlug: string) => {
    handleTracking(tabSlug);
    setSelectedTabSlug(tabSlug);
    setActiveTab(tabViewMap[tabSlug]?.title || '');
    // Not through the router: that would count as a virtual page view.
    updateUrlQueryValues.updateGenericParam(tabSlug, PARAM_NAME);
  };

  const items = useMemo<TabItem[]>(
    () =>
      tabViews.map((tab) => ({
        value: tab.slug,
        label: tab.title,
        content: (
          <ErrorBoundary>
            <SectionContainer>
              <SectionBlocks content={tab.sections || []} />
            </SectionContainer>
          </ErrorBoundary>
        ),
      })),
    [tabViews],
  );

  return <Tabs items={items} value={selectedTabSlug} onChange={handleChange} />;
};
